import { z } from 'zod';

import {
  AnalyticsProvider,
  type AnalyticsDestination,
  type CreateAnalyticsDestinationRequest,
  type Ga4MpConfig,
  type MetaCapiConfig,
  type PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody,
  type PinterestCapiConfig,
  type TikTokEventsConfig,
} from '@nucleus-commerce/api-client/admin';

export const PIXEL_ID = /^\d{5,20}$/;
export const MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/;
export const PIXEL_CODE = /^[A-Z0-9]{10,40}$/;
export const AD_ACCOUNT_ID = /^\d{5,20}$/;
export const TEST_EVENT_CODE_MAX = 40;
export const CREDENTIAL_MIN = 10;
export const CREDENTIAL_MAX = 4000;

export type ProviderConfig =
  | { provider: 'meta_capi'; config: MetaCapiConfig }
  | { provider: 'ga4_mp'; config: Ga4MpConfig }
  | { provider: 'tiktok_events'; config: TikTokEventsConfig }
  | { provider: 'pinterest_capi'; config: PinterestCapiConfig };

/** The config union carries no tag, so each shape is told apart by its identifying key. */
export function providerConfig(destination: AnalyticsDestination): ProviderConfig {
  const { config } = destination;
  if ('pixelId' in config) return { provider: 'meta_capi', config };
  if ('measurementId' in config) return { provider: 'ga4_mp', config };
  if ('pixelCode' in config) return { provider: 'tiktok_events', config };
  return { provider: 'pinterest_capi', config };
}

/** Meta and TikTok both take a test event code, at the same backend path. */
export function hasTestEventCode(provider: AnalyticsProvider): boolean {
  return provider === 'meta_capi' || provider === 'tiktok_events';
}

const IDENTIFIER: Record<
  AnalyticsProvider,
  { field: 'pixelId' | 'measurementId' | 'pixelCode' | 'adAccountId'; pattern: RegExp }
> = {
  meta_capi: { field: 'pixelId', pattern: PIXEL_ID },
  ga4_mp: { field: 'measurementId', pattern: MEASUREMENT_ID },
  tiktok_events: { field: 'pixelCode', pattern: PIXEL_CODE },
  pinterest_capi: { field: 'adAccountId', pattern: AD_ACCOUNT_ID },
};

/**
 * Every provider's config fields live in one object so backend paths (`config.pixelId`) map onto
 * the form as they are; only the selected provider's fields are validated and sent. A new
 * destination needs a credential; on edit an empty one keeps the stored secret.
 */
export function destinationFormSchema(mode: 'create' | 'edit') {
  return z
    .object({
      provider: z.enum(AnalyticsProvider),
      config: z.object({
        pixelId: z.string().trim(),
        testEventCode: z
          .string()
          .trim()
          .max(TEST_EVENT_CODE_MAX, { error: 'analytics:validation.testEventCodeLength' }),
        measurementId: z.string().trim(),
        pixelCode: z.string().trim(),
        adAccountId: z.string().trim(),
        testMode: z.boolean(),
      }),
      credential: z.string().trim(),
    })
    .superRefine((values, ctx) => {
      const { field, pattern } = IDENTIFIER[values.provider];
      const value = values.config[field];
      if (!pattern.test(value)) {
        ctx.addIssue({
          code: 'custom',
          path: ['config', field],
          message: value
            ? `analytics:validation.${field}`
            : `analytics:validation.${field}Required`,
        });
      }
      const { credential } = values;
      if (!credential) {
        if (mode === 'create') {
          ctx.addIssue({
            code: 'custom',
            path: ['credential'],
            message: 'analytics:validation.credentialRequired',
          });
        }
      } else if (credential.length < CREDENTIAL_MIN || credential.length > CREDENTIAL_MAX) {
        ctx.addIssue({
          code: 'custom',
          path: ['credential'],
          message: 'analytics:validation.credentialLength',
        });
      }
    });
}

export type DestinationForm = z.infer<ReturnType<typeof destinationFormSchema>>;

export function emptyDestinationForm(provider: AnalyticsProvider): DestinationForm {
  return {
    provider,
    config: {
      pixelId: '',
      testEventCode: '',
      measurementId: '',
      pixelCode: '',
      adAccountId: '',
      testMode: false,
    },
    credential: '',
  };
}

/** The stored credential is never returned, so the field always starts empty. */
export function fromDestination(destination: AnalyticsDestination): DestinationForm {
  const form = emptyDestinationForm(destination.provider);
  const known = providerConfig(destination);
  switch (known.provider) {
    case 'meta_capi':
      form.config.pixelId = known.config.pixelId;
      form.config.testEventCode = known.config.testEventCode ?? '';
      break;
    case 'ga4_mp':
      form.config.measurementId = known.config.measurementId;
      break;
    case 'tiktok_events':
      form.config.pixelCode = known.config.pixelCode;
      form.config.testEventCode = known.config.testEventCode ?? '';
      break;
    case 'pinterest_capi':
      form.config.adAccountId = known.config.adAccountId;
      form.config.testMode = known.config.testMode;
      break;
  }
  return form;
}

export function toDestinationCreate(values: DestinationForm): CreateAnalyticsDestinationRequest {
  const { config, credential } = values;
  const testEventCode = config.testEventCode ? { testEventCode: config.testEventCode } : {};
  switch (values.provider) {
    case 'meta_capi':
      return {
        provider: 'meta_capi',
        config: { pixelId: config.pixelId, ...testEventCode },
        credential,
      };
    case 'ga4_mp':
      return { provider: 'ga4_mp', config: { measurementId: config.measurementId }, credential };
    case 'tiktok_events':
      return {
        provider: 'tiktok_events',
        config: { pixelCode: config.pixelCode, ...testEventCode },
        credential,
      };
    case 'pinterest_capi':
      return {
        provider: 'pinterest_capi',
        config: { adAccountId: config.adAccountId, testMode: config.testMode },
        credential,
      };
  }
}

function configUpdate(
  values: DestinationForm,
): NonNullable<PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody['config']> {
  const { config } = values;
  const testEventCode = config.testEventCode || null;
  switch (values.provider) {
    case 'meta_capi':
      return { pixelId: config.pixelId, testEventCode };
    case 'ga4_mp':
      return { measurementId: config.measurementId };
    case 'tiktok_events':
      return { pixelCode: config.pixelCode, testEventCode };
    case 'pinterest_capi':
      return { adAccountId: config.adAccountId, testMode: config.testMode };
  }
}

/** An emptied test event code is sent as null so the stored one is cleared. */
export function toDestinationUpdate(
  values: DestinationForm,
): PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody {
  return {
    config: configUpdate(values),
    ...(values.credential ? { credential: values.credential } : {}),
  };
}
